import React, { useState, useCallback, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import { ModernTopbar } from './ModernTopbar';
import { NewSidebar } from './NewSidebar';
import { RingCentralWidget, RINGCENTRAL_PANEL_WIDTH } from '../RingCentral';
import { TaskQueueDrawer } from '../task-queue/TaskQueueDrawer';
import { ReleaseAnnouncementDialog } from '../common/ReleaseAnnouncementDialog';
import { AppShortcuts } from '../Shortcuts/AppShortcuts';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { useUserPreferencesStore } from '../../stores/userPreferencesStore';
// import RingCentralAutoAuth from '../RingCentral/RingCentralAutoAuth';
// import RingCentralWidgetAuth from '../RingCentral/RingCentralWidgetAuth';
// import CustomPhoneWidget from '../RingCentral/CustomPhoneWidget';

const TOPBAR_HEIGHT = 64;
const MAIN_LAYOUT_TOP_PADDING = 40;

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const { isOpen, collapsed, width, isMobile, toggleSidebar, toggleCollapse, closeSidebar } = useSidebar();

  const [touchStart, setTouchStart] = useState(0);
  const [ringCentralOpen, setRingCentralOpen] = useState(false);

  // Load user preferences app-wide (theme mode, workflow defaults) — without
  // this, the saved theme only applied after visiting the Profile page.
  const activePharmacyId = usePharmacyStore((state) => state.activePharmacy?.id);
  const fetchPreferences = useUserPreferencesStore((state) => state.fetchPreferences);
  useEffect(() => {
    if (activePharmacyId) {
      fetchPreferences(activePharmacyId);
    }
  }, [activePharmacyId, fetchPreferences]);

  // Calculate drawer width for desktop only
  const getDrawerWidth = () => {
    if (isMobile || !isOpen) return 0;
    return width;
  };

  // Calculate RingCentral panel width for desktop only
  const getRingCentralWidth = () => {
    if (isMobile || !ringCentralOpen) return 0;
    return RINGCENTRAL_PANEL_WIDTH;
  };

  // Handle RingCentral open state change
  const handleRingCentralOpenChange = useCallback((open: boolean) => {
    setRingCentralOpen(open);
  }, []);

  // Handle touch events for swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEnd = e.changedTouches[0].clientX;
    const swipeDistance = touchEnd - touchStart;

    // Swipe right to open (from left edge)
    if (!isOpen && touchStart < 20 && swipeDistance > 100) {
      toggleSidebar();
    }
    // Swipe left to close
    else if (isOpen && swipeDistance < -100) {
      closeSidebar();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw',
      }}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      {/* Fixed Topbar */}
      <ModernTopbar
        onDrawerToggle={toggleSidebar}
        drawerWidth={0} // Always full width
        topbarHeight={TOPBAR_HEIGHT}
        sidebarHidden={!isOpen && !isMobile}
      />

      {/* Mobile Sidebar (Overlay) */}
      {isMobile && (
        <NewSidebar
          variant="temporary"
          open={isOpen}
          onClose={closeSidebar}
          width={width}
        />
      )}

      {/* Desktop Sidebar (Fixed Position) */}
      {!isMobile && isOpen && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            width: width,
            zIndex: theme.zIndex.drawer,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <NewSidebar
            variant="permanent"
            open={true}
            width={width}
            collapsed={collapsed}
            onCollapseToggle={toggleCollapse}
          />
        </Box>
      )}
      
      {/* Main Content - Always full width with dynamic padding */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          maxWidth: '100%',
          paddingTop: `${MAIN_LAYOUT_TOP_PADDING}px`,
          paddingLeft: isMobile ? 0 : `${getDrawerWidth()}px`,
          paddingRight: isMobile ? 0 : `${getRingCentralWidth()}px`,
          bgcolor: 'background.default',
          minHeight: '100vh',
          transition: theme.transitions.create(['padding-left', 'padding-right', 'padding-bottom'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Box sx={{
          p: {
            xs: 2,
            sm: 2.5,
            md: 3
          },
          height: '100%',
          width: '100%',
          maxWidth: '100%',
        }}>
          {children}
        </Box>
      </Box>
      
      {/* Touch edge for swipe gesture on mobile */}
      {isMobile && !isOpen && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: TOPBAR_HEIGHT,
            bottom: 0,
            width: 20,
            zIndex: theme.zIndex.drawer - 1,
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        />
      )}

      {/* App-wide keyboard shortcuts + the Shift+? cheat sheet (GP-482).
          Mounted here so the keys survive hiding the sidebar (Ctrl+B) and
          every route change. */}
      <AppShortcuts />

      {/* Task Queue Drawer - globally accessible */}
      <TaskQueueDrawer />

      {/* One-time release announcement (per user) */}
      <ReleaseAnnouncementDialog />

      {/* RingCentral Widget with OAuth - Only render if enabled */}
      {import.meta.env.VITE_RINGCENTRAL_ENABLED === 'true' && (
        <RingCentralWidget
          position="right"
          onOpenChange={handleRingCentralOpenChange}
        />
      )}
    </Box>
  );
};